import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import axios from 'axios';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import FilesService, { IUploadFileUrl } from '@services/FilesService';
import { env } from 'env';
import { useTranslation } from 'react-i18next';

import { useStyles } from './styles';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';
import BrandingsService from '@services/BrandingsService';

type CustomSpotsEditorProps = {
  open: boolean;
  onClose: () => void;
};

const CustomSpotsEditor: React.FC<CustomSpotsEditorProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    currentRoom,
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    selectedCustomizationUeId,
    setSelectedCustomizationUeId,
    selectedCustomizationTargetSize,
    setSelectedCustomizationTargetSize,
    setShowCustomSpotsEditor,
    isBrandingModeEnabled,
    currentBrandingId,
    currentParticipant,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedAsset, setUploadedAsset] = useState<IUploadFileUrl | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedImageSize, setSelectedImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isFailureOpen, setIsFailureOpen] = useState(false);
  const [failureDetailsOpen, setFailureDetailsOpen] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>('');
  const [isAssetUpdatedOpen, setIsAssetUpdatedOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const previousSpotSnapshotRef = useRef<{
    existed: boolean;
    spotId: string | null;
    dataPayload: { location?: string; url?: string; md5?: string } | null;
    previousUrl: string | null;
  } | null>(null);
  const lastUpdatedSpotIdRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const ALLOWED_MIME_TYPES = ['image/png'];

  const resetState = useCallback(() => {
    setIsDragging(false);
    setIsUploading(false);
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    setSelectedImageSize(null);
    setIsFailureOpen(false);
    setFailureDetailsOpen(false);
    setFailureMessage('');
    setIsAssetUpdatedOpen(false);
    setIsReverting(false);
    previousSpotSnapshotRef.current = null;
    lastUpdatedSpotIdRef.current = null;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  }, [previewUrl]);

  const openFailure = useCallback((message: string) => {
    setFailureMessage(message);
    setFailureDetailsOpen(false);
    setIsFailureOpen(true);
  }, []);

  const targetWidth = selectedCustomizationTargetSize?.width || null;
  const targetHeight = selectedCustomizationTargetSize?.height || null;
  const targetAspect =
    targetWidth && targetHeight ? targetWidth / targetHeight : 16 / 9;

  const unsupportedFormatText = useMemo(
    () =>
      t('customSpotsEditor.failureUnsupportedFormat', {
        defaultValue:
          'Unsupported format. Please upload a PNG file and try again.',
      }),
    [t],
  );

  const requirementsText = useMemo(() => {
    const reqParts: string[] = [];
    reqParts.push(
      t('customSpotsEditor.requirements.format', {
        defaultValue: 'Supported format: PNG',
      }),
    );
    if (targetWidth && targetHeight) {
      reqParts.push(
        t('customSpotsEditor.requirements.size', {
          defaultValue: 'Size: {{w}}×{{h}}',
          w: targetWidth,
          h: targetHeight,
        }),
      );
    }
    return reqParts.join('   ');
  }, [t, targetHeight, targetWidth]);

  const validateSelectedFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        openFailure(unsupportedFormatText);
        return false;
      }

      return true;
    },
    [ALLOWED_MIME_TYPES, openFailure, unsupportedFormatText],
  );

  const handleFiles = useCallback(
    async (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);
      if (!file.type.startsWith('image/')) {
        openFailure(unsupportedFormatText);
        return;
      }

      const isValid = await validateSelectedFile(file);
      if (!isValid) {
        // Keep editor open, but clear selection
        setSelectedFile(null);
        setSelectedImageSize(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setError(null);
      setUploadedAsset(null);
      setSelectedFile(file);
      setSelectedImageSize(null);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [openFailure, previewUrl, t, validateSelectedFile],
  );

  const spotsSource = useMemo(
    () =>
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots,
    [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots],
  );

  const existingSpotImageUrl = useMemo(() => {
    if (!selectedCustomizationUeId) {
      return null;
    }

    const spotMatch = spotsSource.find(
      (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
    );

    const url = spotMatch?.data?.url || spotMatch?.data?.location || null;

    if (typeof url !== 'string' || !url.length) {
      return null;
    }

    const cacheKey =
      (spotMatch as any)?.timestamp ??
      (spotMatch as any)?.updatedAt ??
      (spotMatch as any)?.data?.md5;

    if (!cacheKey) {
      return url;
    }

    // const separator = url.includes('?') ? '&' : '?';
    return url;
  }, [spotsSource, selectedCustomizationUeId]);

  const displayedPreviewUrl = previewUrl || existingSpotImageUrl || null;
  const hasPreview = !!displayedPreviewUrl;
  const hasSelectedFile = !!selectedFile;
  const isReadyToUpload = hasSelectedFile && !isUploading;

  const handleSelectClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;
    if (!currentRoom) {
      setError(t('customSpotsEditor.errorNoRoom', 'Room is not available.'));
      return;
    }
    if (!selectedCustomizationUeId) {
      setError(
        t('customSpotsEditor.errorNoSelection', 'No spot selected for update.'),
      );
      return;
    }
    setIsUploading(true);
    setError(null);
    try {
      const [uploadInfo] = await FilesService.uploadFiles([selectedFile]);
      const dataPayload: any = {
        location: uploadInfo.location,
        url: uploadInfo.url,
      };
      if ((uploadInfo as any).md5) {
        dataPayload.md5 = (uploadInfo as any).md5;
      }

      const spotMatch = spotsSource.find(
        (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
      );
      const existingBrandingSpotId: string | null =
        (spotMatch?.brandingSpotId as string | undefined) ||
        (spotMatch?.id as string | undefined) ||
        null;
      const existingRoomSpotId: string | null =
        (spotMatch?.roomSpotId as string | undefined) ||
        (spotMatch?.id as string | undefined) ||
        null;

      const previousData = (spotMatch as any)?.data || null;
      const previousPayload: {
        location?: string;
        url?: string;
        md5?: string;
      } | null =
        previousData && typeof previousData === 'object'
          ? {
              ...(typeof previousData.location === 'string'
                ? { location: previousData.location }
                : {}),
              ...(typeof previousData.url === 'string'
                ? { url: previousData.url }
                : {}),
              ...(typeof previousData.md5 === 'string'
                ? { md5: previousData.md5 }
                : {}),
            }
          : null;
      const previousUrl: string | null =
        (typeof previousData?.url === 'string' && previousData.url.length
          ? previousData.url
          : null) ||
        (typeof previousData?.location === 'string' &&
        previousData.location.length
          ? previousData.location
          : null);
      previousSpotSnapshotRef.current = {
        existed: !!spotMatch,
        spotId: existingBrandingSpotId || existingRoomSpotId,
        dataPayload: previousPayload,
        previousUrl,
      };

      const useBrandingSpots = currentBrandingId && isBrandingModeEnabled;

      if (useBrandingSpots && currentBrandingId) {
        let updatedSpot: any = null;
        try {
          if (spotMatch && existingBrandingSpotId) {
            updatedSpot = await BrandingsService.patchBrandingSpot(
              currentBrandingId,
              existingBrandingSpotId,
              { data: dataPayload, isActive: true },
            );
          } else {
            updatedSpot = await BrandingsService.createBrandingSpot(
              currentBrandingId,
              {
                isActive: true,
                type: 'IMAGE',
                name: selectedCustomizationUeId,
                ueIdentifier: selectedCustomizationUeId,
                roomSpotId: existingRoomSpotId ?? undefined,
                roomId: currentRoom.roomId,
                data: {
                  ...dataPayload,
                  ueName: selectedCustomizationUeId,
                  mapId: currentRoom.map?.mapId,
                  updatedBy: currentParticipant
                    ? {
                        participantId: (currentParticipant as any)
                          ?.participantId,
                        name: (currentParticipant as any)?.name,
                        email: (currentParticipant as any)?.email,
                      }
                    : undefined,
                },
              },
            );
          }
        } catch (patchError: any) {
          if (patchError?.response?.status !== 404) {
            throw patchError;
          }
          updatedSpot = await BrandingsService.createBrandingSpot(
            currentBrandingId,
            {
              isActive: true,
              type: 'IMAGE',
              name: selectedCustomizationUeId,
              ueIdentifier: selectedCustomizationUeId,
              roomId: currentRoom.roomId,
              data: {
                ...dataPayload,
                ueName: selectedCustomizationUeId,
                mapId: currentRoom.map?.mapId,
                updatedBy: currentParticipant
                  ? {
                      participantId: (currentParticipant as any)?.participantId,
                      name: (currentParticipant as any)?.name,
                      email: (currentParticipant as any)?.email,
                    }
                  : undefined,
              },
            },
          );
        }

        const refreshed = await BrandingsService.getBrandingSpots(
          currentBrandingId,
          { limit: 200 },
        );
        setBrandingSpots(refreshed.spots ?? []);

        if (updatedSpot) {
          lastUpdatedSpotIdRef.current =
            (updatedSpot as any)?.brandingSpotId ||
            (updatedSpot as any)?.id ||
            null;
          sendMessageToUnreal({
            action: 'SPOT_UPDATED',
            spot: {
              ...updatedSpot,
              ueIdentifier:
                updatedSpot.ueIdentifier || selectedCustomizationUeId,
            },
          });
        }
      } else {
        const roomSpotId =
          (spotMatch?.roomSpotId as string | undefined) ||
          (spotMatch?.id as string | undefined) ||
          null;

        const patchRoomSpot = async () => {
          if (!spotMatch || !roomSpotId) return false;
          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${roomSpotId}`,
            { data: dataPayload, isActive: true },
          );
          return true;
        };

        let updatedViaPatch = false;
        try {
          updatedViaPatch = await patchRoomSpot();
        } catch (patchError: any) {
          if (patchError?.response?.status !== 404) {
            throw patchError;
          }
        }

        if (!updatedViaPatch) {
          const createRes = await axios.post(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots`,
            {
              name: selectedCustomizationUeId,
              type: 'IMAGE',
              ueIdentifier: selectedCustomizationUeId,
              isActive: true,
              data: dataPayload,
            },
          );
          const created =
            (createRes as any)?.data?.roomSpot || (createRes as any)?.data;
          const createdId: string | null =
            created?.roomSpotId || created?.id || null;
          if (createdId) {
            lastUpdatedSpotIdRef.current = createdId;
          }
        }

        const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
        setRoomSpots(refreshed);

        const updatedSpot =
          refreshed.find(
            (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
          ) || null;

        if (updatedSpot) {
          lastUpdatedSpotIdRef.current =
            (updatedSpot as any)?.roomSpotId ||
            (updatedSpot as any)?.id ||
            null;
          sendMessageToUnreal({
            action: 'SPOT_UPDATED',
            spot: updatedSpot,
          });
        }
      }

      setUploadedAsset(uploadInfo);
      setIsAssetUpdatedOpen(true);
      setShowCustomSpotsEditor(false);
    } catch (uploadError) {
      setError(
        t(
          'customSpotsEditor.errorUpload',
          'Failed to upload file, please try again.',
        ),
      );
    } finally {
      setIsUploading(false);
    }
  }, [
    currentBrandingId,
    currentParticipant,
    currentRoom,
    isBrandingModeEnabled,
    spotsSource,
    selectedCustomizationUeId,
    selectedFile,
    sendMessageToUnreal,
    setRoomSpots,
    setBrandingSpots,
    setShowCustomSpotsEditor,
    t,
  ]);

  const handleSaveAssetUpdated = () => {
    setIsAssetUpdatedOpen(false);
    handleClose();
  };

  const handleRevertAssetUpdated = useCallback(async () => {
    if (!currentRoom || !selectedCustomizationUeId) {
      handleSaveAssetUpdated();
      return;
    }
    setIsReverting(true);
    try {
      const snapshot = previousSpotSnapshotRef.current;
      const useBrandingSpots = currentBrandingId && isBrandingModeEnabled;

      if (!snapshot?.existed) {
        const spotIdToDelete =
          lastUpdatedSpotIdRef.current ||
          (
            spotsSource.find(
              (s: any) => s.ueIdentifier === selectedCustomizationUeId,
            ) as any
          )?.brandingSpotId ||
          (
            spotsSource.find(
              (s: any) => s.ueIdentifier === selectedCustomizationUeId,
            ) as any
          )?.roomSpotId ||
          (
            spotsSource.find(
              (s: any) => s.ueIdentifier === selectedCustomizationUeId,
            ) as any
          )?.id ||
          null;

        if (spotIdToDelete) {
          if (useBrandingSpots && currentBrandingId) {
            await BrandingsService.deleteBrandingSpot(
              currentBrandingId,
              spotIdToDelete,
            ).catch(() => undefined);
          } else {
            await axios
              .delete(
                `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${spotIdToDelete}`,
              )
              .catch(() => undefined);
          }
        }
      } else if (snapshot?.spotId && snapshot.dataPayload) {
        let revertPayload: {
          location?: string;
          url?: string;
          md5?: string;
        } | null = snapshot.dataPayload;

        if (snapshot.previousUrl) {
          try {
            const resp = await fetch(snapshot.previousUrl);
            const blob = await resp.blob();
            const file = new File(
              [blob],
              `spot-${selectedCustomizationUeId}.png`,
              { type: blob.type || 'image/png' },
            );
            const [uploadInfo] = await FilesService.uploadFiles([file]);
            revertPayload = {
              location: uploadInfo.location,
              url: uploadInfo.url,
              ...(((uploadInfo as any).md5 && {
                md5: (uploadInfo as any).md5,
              }) ||
                {}),
            };
          } catch {
            // fall back to stored payload
          }
        }

        if (useBrandingSpots && currentBrandingId) {
          await BrandingsService.patchBrandingSpot(
            currentBrandingId,
            snapshot.spotId,
            { isActive: true, data: revertPayload || {} },
          );
        } else {
          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${snapshot.spotId}`,
            {
              isActive: true,
              data: revertPayload || {},
            },
          );
        }
      } else {
        handleSaveAssetUpdated();
        return;
      }

      if (useBrandingSpots && currentBrandingId) {
        const refreshed = await BrandingsService.getBrandingSpots(
          currentBrandingId,
          { limit: 200 },
        );
        setBrandingSpots(refreshed.spots ?? []);
        const revertedSpot =
          (refreshed.spots ?? []).find(
            (s: any) => s.ueIdentifier === selectedCustomizationUeId,
          ) || null;
        if (revertedSpot) {
          sendMessageToUnreal({
            action: 'SPOT_UPDATED',
            spot: revertedSpot,
          });
        }
      } else {
        const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
        setRoomSpots(refreshed);
        const revertedSpot =
          refreshed.find(
            (spot: any) => spot.ueIdentifier === selectedCustomizationUeId,
          ) || null;
        if (revertedSpot) {
          sendMessageToUnreal({
            action: 'SPOT_UPDATED',
            spot: revertedSpot,
          });
        }
      }
    } catch {
      setError(
        t(
          'customSpotsEditor.revertFailed',
          'Failed to revert, please try again.',
        ),
      );
    } finally {
      setIsReverting(false);
      setIsAssetUpdatedOpen(false);
      handleClose();
    }
  }, [
    currentBrandingId,
    currentRoom,
    isBrandingModeEnabled,
    spotsSource,
    selectedCustomizationUeId,
    sendMessageToUnreal,
    setRoomSpots,
    setBrandingSpots,
  ]);

  const handleClose = () => {
    resetState();
    setSelectedCustomizationUeId(null);
    setSelectedCustomizationTargetSize(null);
    setShowCustomSpotsEditor(false);
    onClose();
  };

  const handleTryAgain = () => {
    setIsFailureOpen(false);
    setFailureDetailsOpen(false);
    handleSelectClick();
  };

  const handleLoadAnother = () => {
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    setSelectedImageSize(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    handleSelectClick();
  };

  const handleCopySpotId = async () => {
    if (!selectedCustomizationUeId) return;
    try {
      await navigator.clipboard.writeText(selectedCustomizationUeId);
    } catch {
      // ignore
    }
  };

  const dialogTitle = selectedFile
    ? t('customSpotsEditor.customizeTitle', { defaultValue: 'Customize Spot' })
    : t('customSpotsEditor.updateTitle', { defaultValue: 'Update Spot Media' });

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: classes.paper,
        }}
      >
        <DialogTitle sx={classes.title}>
          {dialogTitle}
          <IconButton
            sx={classes.closeButton}
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={classes.content}>
          <Box
            sx={{
              ...classes.dropZone,
              ...(isDragging ? classes.dropZoneActive : {}),
              ...(hasPreview ? classes.previewZone : {}),
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={!selectedFile ? handleSelectClick : undefined}
          >
            {displayedPreviewUrl ? (
              <Box
                sx={{
                  ...classes.previewFrame,
                  aspectRatio: `${targetAspect}`,
                }}
              >
                <Box
                  component="img"
                  src={displayedPreviewUrl}
                  alt="preview"
                  sx={classes.previewImage}
                  onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      setSelectedImageSize({
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                      });
                    }
                  }}
                />
              </Box>
            ) : (
              <>
                <UploadFileIcon sx={classes.uploadIcon} />
                <Typography sx={classes.dropZoneTitle}>
                  {t(
                    'customSpotsEditor.dropTitle',
                    'Drag and drop an image here',
                  )}
                </Typography>
                <Typography sx={classes.dropZoneSubtitle}>
                  {t(
                    'customSpotsEditor.dropSubtitle',
                    'or browse and select the file manually',
                  )}
                </Typography>
                <Button
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectClick();
                  }}
                  disabled={isUploading}
                  sx={classes.linkButton}
                >
                  {t('customSpotsEditor.uploadLink', 'Upload')}
                </Button>
              </>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => handleFiles(event.target.files)}
            />
          </Box>

          <Box sx={classes.requirementsRow}>
            <Box sx={classes.requirementsLeft}>
              <Typography sx={classes.requirementsText}>
                {t('customSpotsEditor.spotId', { defaultValue: 'Spot ID:' })}{' '}
                <Box component="span" sx={classes.requirementsStrong}>
                  {selectedCustomizationUeId ||
                    t('customSpotsEditor.none', '—')}
                </Box>
              </Typography>
              {selectedCustomizationUeId ? (
                <IconButton
                  size="small"
                  onClick={handleCopySpotId}
                  sx={classes.copyButton}
                  aria-label={t('common.copy', 'Copy')}
                >
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              ) : null}
            </Box>
            <Typography sx={classes.requirementsText}>
              {requirementsText}
            </Typography>
          </Box>

          {error ? (
            <Typography sx={classes.errorText} variant="body2">
              {error}
            </Typography>
          ) : null}

          {isUploading ? (
            <Box sx={classes.statusRow}>
              <CircularProgress size={18} />
              <Typography sx={classes.statusText}>
                {t(
                  'customSpotsEditor.uploading',
                  'Uploading file and updating spot...',
                )}
              </Typography>
            </Box>
          ) : null}
        </DialogContent>

        <Box sx={classes.footer}>
          {!hasPreview ? (
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isUploading}
              sx={classes.footerSingleButton}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          ) : (
            <Box sx={classes.footerButtons}>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!isReadyToUpload}
                sx={classes.primaryButton}
              >
                {t('customSpotsEditor.loadToWorld', {
                  defaultValue: 'Load to Experience',
                })}
              </Button>
              <Button
                variant="outlined"
                onClick={handleLoadAnother}
                disabled={isUploading}
                sx={classes.secondaryButton}
              >
                {t('customSpotsEditor.loadAnother', {
                  defaultValue: 'Load another asset',
                })}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isUploading}
                sx={classes.tertiaryButton}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>

      <Dialog
        open={isFailureOpen}
        onClose={() => setIsFailureOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: classes.failurePaper }}
      >
        <DialogTitle sx={classes.failureTitle}>
          {t('customSpotsEditor.failureTitle', {
            defaultValue: "Asset doesn't match",
          })}
          <IconButton
            sx={classes.failureCloseButton}
            onClick={() => setIsFailureOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={classes.failureContent}>
          <Typography sx={classes.failureText}>
            {failureMessage || unsupportedFormatText}
          </Typography>
          {failureDetailsOpen ? (
            <Typography sx={classes.failureDetails}>
              {requirementsText}
            </Typography>
          ) : null}
          <Box sx={classes.failureActions}>
            <Button
              variant="outlined"
              onClick={() => setFailureDetailsOpen((v) => !v)}
              sx={classes.failureMoreInfoButton}
            >
              {t('customSpotsEditor.moreInfo', { defaultValue: 'More info' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleTryAgain}
              sx={classes.failureTryAgainButton}
            >
              {t('customSpotsEditor.tryAgain', { defaultValue: 'Try again' })}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAssetUpdatedOpen}
        onClose={handleSaveAssetUpdated}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: classes.assetUpdatedPaper }}
      >
        <DialogTitle sx={classes.assetUpdatedTitle}>
          {t('customSpotsEditor.assetUpdatedTitle', {
            defaultValue: 'Asset updated',
          })}
          <IconButton
            sx={classes.assetUpdatedCloseButton}
            onClick={handleSaveAssetUpdated}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={classes.assetUpdatedContent}>
          <Typography sx={classes.assetUpdatedText}>
            {t('customSpotsEditor.assetUpdatedText', {
              defaultValue:
                'Would you like to save this asset or revert to the previous one?',
            })}
          </Typography>
          <Box sx={classes.assetUpdatedActions}>
            <Button
              variant="outlined"
              onClick={handleRevertAssetUpdated}
              disabled={isReverting}
              sx={classes.assetUpdatedRevertButton}
            >
              {t('customSpotsEditor.revert', { defaultValue: 'Revert' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAssetUpdated}
              disabled={isReverting}
              sx={classes.assetUpdatedSaveButton}
            >
              {t('common.save', { defaultValue: 'Save' })}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomSpotsEditor;
